import { SUN_GALACTIC_ORBIT } from "../data/galactic";
import type { Body } from "../data/schema";
import { bodyName, fill, formatNumber, messages, plural, type Lang } from "../i18n/index";
import { bodySubtitle, kindLabel, type ParentRef } from "./bodies";
import { blurOnPointerClick, localize } from "./dom";
import {
  formatDensity,
  formatDistance,
  formatGravity,
  formatInclination,
  formatKmPerSecond,
  formatMass,
  formatTemperature,
  orbitalSpeedKmS,
} from "./facts";
import { INFO_TABS, type InfoTab, type Store, type ViewState } from "./store";
import { mountTabs } from "./tabs";

function period(days: number, lang: Lang): string {
  const m = messages(lang).info;
  if (days < 365.25) return plural(lang, m.days, days, 2);
  return plural(lang, m.years, days / 365.25, 1);
}

/** Rotation period in hours below two days, else days; negative periods are retrograde. */
export function formatDay(hours: number, lang: Lang): string {
  const m = messages(lang).info;
  const abs = Math.abs(hours);
  const text =
    abs < 48
      ? fill(m.hours, { count: formatNumber(lang, abs, 1) })
      : plural(lang, m.days, abs / 24, 1);
  return hours < 0 ? fill(m.retrograde, { period: text }) : text;
}

type Fact = [label: string, value: string];

export interface BodyFacts {
  title: string;
  kind: string;
  subtitle: string;
  /** Null when the size was never measured. */
  radius: string | null;
  /** A line above the Orbit rows; only the Sun has one. */
  orbitNote: string | null;
  tabs: Record<InfoTab, Fact[]>;
}

/** Moons smaller than this are irregular lumps; the data has no shape flag. */
const IRREGULAR_MOON_RADIUS_KM = 200;

const INCLINATION_LABEL = { equator: "inclinationEquator", laplace: "inclinationLaplace" } as const;

/** The Orbit rows, the period text the Overview reuses, and the Sun's note. */
function orbitFacts(body: Body, lang: Lang): { period: string; note: string | null; rows: Fact[] } {
  const m = messages(lang).info;
  const { orbit } = body;
  if (!orbit) {
    const about = (value: string): string => fill(m.about, { value });
    const g = SUN_GALACTIC_ORBIT;
    // the galactic figures are the Sun's own; any other orbitless body (a future star) has none
    if (body.id !== "sun") {
      return {
        period: m.unknown,
        note: null,
        rows: [m.distance, m.period, m.eccentricity, m.inclination, m.speed].map((label) => [
          label,
          m.unknown,
        ]),
      };
    }
    const galacticPeriod = about(plural(lang, m.millionYears, g.periodYears / 1e6));
    return {
      period: galacticPeriod,
      note: m.galacticOrbit,
      rows: [
        [m.distance, about(plural(lang, m.lightYears, g.distanceLy))],
        [m.period, galacticPeriod],
        [m.eccentricity, m.unknown],
        [m.inclination, m.unknown],
        [m.speed, about(formatKmPerSecond(g.speedKmS, lang, 0))],
      ],
    };
  }
  const { inclination } = orbit;
  const orbitalPeriod = period(orbit.periodDays, lang);
  return {
    period: orbitalPeriod,
    note: null,
    rows: [
      [m.distance, formatDistance(orbit.a, body.parent === "sun", lang)],
      [m.period, orbitalPeriod],
      [m.eccentricity, formatNumber(lang, orbit.e, 3)],
      [
        m[inclination ? INCLINATION_LABEL[inclination.plane] : "inclination"],
        formatInclination(inclination?.deg ?? orbit.i, lang),
      ],
      [m.speed, formatKmPerSecond(orbitalSpeedKmS(orbit.a, orbit.e, orbit.periodDays), lang)],
    ],
  };
}

/** Card content for a body, one row list per tab; a missing value reads "unknown". */
export function formatBodyFacts(
  body: Body,
  parent: ParentRef | null,
  moonCount: number,
  lang: Lang,
): BodyFacts {
  const m = messages(lang).info;
  const known = (value: string | null): string => value ?? m.unknown;
  const { radiusKm, radiusUnknown, gm, densityGcm3, meanTempC, rotationPeriodHours, axialTiltDeg } =
    body.physical;
  const radius = radiusUnknown ? null : `${formatNumber(lang, radiusKm)} km`;
  // planets carry equatorial radii (src/data/planets.ts); every other body a mean radius
  const radiusRow: Fact = [body.kind === "planet" ? m.equatorialRadius : m.radius, known(radius)];
  const circumference = `${formatNumber(lang, 2 * Math.PI * radiusKm)} km`;
  const equator = radiusUnknown
    ? null
    : body.kind === "moon" && radiusKm < IRREGULAR_MOON_RADIUS_KM
      ? fill(m.about, { value: circumference })
      : circumference;
  const orbit = orbitFacts(body, lang);
  const day = rotationPeriodHours === null ? null : formatDay(rotationPeriodHours, lang);
  const overview: Fact[] = [radiusRow, [m.day, known(day)], [m.year, orbit.period]];
  if (moonCount > 0) overview.push([m.moons, formatNumber(lang, moonCount)]);
  return {
    title: bodyName(lang, body),
    kind: kindLabel(body.kind, lang),
    subtitle: bodySubtitle(body.kind, parent, lang),
    radius,
    orbitNote: orbit.note,
    tabs: {
      overview,
      body: [
        radiusRow,
        [m.equator, known(equator)],
        [m.mass, known(gm === null ? null : formatMass(gm, lang))],
        // the placeholder radius of an unmeasured moon must not feed GM/r²
        [m.gravity, known(gm === null || radiusUnknown ? null : formatGravity(gm, radiusKm, lang))],
        [m.density, known(densityGcm3 === null ? null : formatDensity(densityGcm3, lang))],
        [m.temperature, known(meanTempC === null ? null : formatTemperature(meanTempC, lang))],
        [
          m.axialTilt,
          known(axialTiltDeg === null ? null : `${formatNumber(lang, axialTiltDeg, 1, 0)}°`),
        ],
      ],
      orbit: orbit.rows,
    },
  };
}

export interface InfoChip {
  show(body: Body, parent: Body | null, moonCount: number): void;
}

/** Top-left chip with the focused body's name; tapping it expands a fact card. */
export function mountInfoChip(
  container: HTMLElement,
  store: Store,
  onShowMoons: (id: string) => void,
): InfoChip {
  const root = document.createElement("div");
  root.className = "info glass";
  const toggle = document.createElement("button");
  toggle.type = "button";
  toggle.className = "info-toggle";
  toggle.setAttribute("aria-controls", "info-card");
  const name = document.createElement("span");
  name.className = "info-name";
  const kind = document.createElement("span");
  kind.className = "info-kind";
  const radius = document.createElement("span");
  radius.className = "info-radius";
  const caret = document.createElement("span");
  caret.className = "info-caret";
  toggle.append(name, kind, radius, caret);
  toggle.addEventListener("click", () => store.set({ infoExpanded: !store.state.infoExpanded }));
  blurOnPointerClick(toggle);

  const card = document.createElement("div");
  card.id = "info-card";
  card.className = "info-card";
  const subtitle = document.createElement("div");
  subtitle.className = "info-subtitle";
  const tabs = mountTabs(INFO_TABS, "info", (tab) => store.set({ infoTab: tab }));
  // a clicked tab would swallow the window shortcuts, which skip buttons, as on the chip
  tabs.tablist.querySelectorAll<HTMLElement>('[role="tab"]').forEach((t) => blurOnPointerClick(t));
  const lists = {} as Record<InfoTab, HTMLElement>;
  for (const tab of INFO_TABS) {
    lists[tab] = document.createElement("dl");
    lists[tab].className = "info-facts";
    tabs.panels[tab].append(lists[tab]);
  }
  const orbitNote = document.createElement("div");
  orbitNote.className = "info-note";
  tabs.panels.orbit.prepend(orbitNote);
  let current: { body: Body; parent: Body | null; moonCount: number } | null = null;
  const moons = document.createElement("button");
  moons.type = "button";
  moons.className = "info-moons";
  moons.addEventListener("click", () => {
    if (current) onShowMoons(current.body.id);
  });
  tabs.panels.overview.append(moons);
  card.append(subtitle, tabs.tablist, ...INFO_TABS.map((tab) => tabs.panels[tab]));
  root.append(toggle, card);
  // the window shortcut handler ignores keys aimed at buttons, so collapse on Esc here
  root.addEventListener("keydown", (e) => {
    if (e.key !== "Escape" || !store.state.infoExpanded) return;
    store.set({ infoExpanded: false });
    e.preventDefault();
  });
  container.prepend(root);

  const sync = (s: ViewState): void => {
    card.hidden = !s.infoExpanded;
    root.classList.toggle("expanded", s.infoExpanded);
    toggle.setAttribute("aria-expanded", String(s.infoExpanded));
    caret.textContent = s.infoExpanded ? "▴" : "▾";
    tabs.sync(s.infoTab, messages(s.lang).info.tabs);
  };
  store.subscribe(sync);
  sync(store.state);

  const render = (lang: Lang): void => {
    if (!current) return;
    const { body, parent, moonCount } = current;
    const f = formatBodyFacts(
      body,
      parent && { id: parent.id, name: bodyName(lang, parent) },
      moonCount,
      lang,
    );
    name.textContent = f.title;
    kind.textContent = `· ${f.kind}`;
    radius.textContent = f.radius ? `· ${f.radius}` : "";
    radius.hidden = f.radius === null;
    subtitle.textContent = f.subtitle;
    for (const tab of INFO_TABS)
      lists[tab].replaceChildren(
        ...f.tabs[tab].flatMap(([label, value]) => {
          const dt = document.createElement("dt");
          dt.textContent = label;
          const dd = document.createElement("dd");
          dd.textContent = value;
          return [dt, dd];
        }),
      );
    orbitNote.textContent = f.orbitNote ?? "";
    orbitNote.hidden = f.orbitNote === null;
    moons.hidden = moonCount === 0;
  };
  localize(store, (m, lang) => {
    moons.textContent = m.info.showMoons;
    tabs.tablist.setAttribute("aria-label", m.info.tabsLabel);
    render(lang);
  });

  return {
    show(body, parent, moonCount) {
      current = { body, parent, moonCount };
      render(store.state.lang);
    },
  };
}
